import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { useAuthStore } from '@/stores/auth';
import { crud } from '@/common/crud';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { Appointment, Encounter } from '@/fhirworks';
import timezoneApi from '@/common/api/timezone.api';
import { addDays, addMinutes, differenceInMinutes, format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { RRule } from 'rrule';
import { isPatient } from '@/common/core';
import { fetchRelationshipsForSubject, buildJobTitleCredentials } from '@/common/participantRelationships';
import EncounterDiagnosis from '@/fhirworks/EncounterDiagnosis';
import { useTaskFlow } from '@/common/workflowEngine/useTaskFlow';
import { getDefaultSupervisorParticipant } from '@/common/workflowEngine/useEncounterFlow';

const { postTransaction } = useWorkflow();
const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const createEvent = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (data.appointment.seriesDescription) {
        transaction = await addRecurringEvents(data.appointment, transaction);
    } else {
        transaction.push(crud.post(data.appointment));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const updateEvent = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (!data.appointment.seriesId && data.appointment.seriesDescription) {
        transaction = await convertEventToRecurring(data.appointment);
    } else if (data.appointment.seriesId) {
        // if beginning an instance of a series then detach it and save
        if (data.appointment.status === 'checked-in' || data.appointment.status === 'fulfilled') {
            data.appointment.seriesId = undefined;
            data.appointment.seriesDescription = undefined;
            transaction.push(crud.patch(data.appointment));
        } else {
            transaction = await updateRecurringEvent(data, transaction);
        }
    } else {
        transaction.push(crud.patch(data.appointment));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const deleteEvent = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    let events = Array.isArray(data.appointment) ? data.appointment : [data.appointment];

    // if recurring then remove all pending as well
    if (data.appointment.seriesId && data.applyTo === 'pending') {
        events = await getPendingEvents(data.appointment);
    }
    // delete all specified events
    events.forEach((event) => {
        transaction.push(crud.delete(event));
    });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const beginEvent = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const appointment = data.appointment;
    // determine owner and assignments
    const owner = getOwner(appointment);
    const assignee = appointment.participant.filter((item) => item?.actor?.resourceType === 'Practitioner')?.map((item) => item?.actor) || [];
    // process flow for each target resource

    transaction = await updateEvent({ appointment }, transaction);

    if (data.appointment.form?.length) {
        transaction = await addFormTask(
            {
                task: {
                    owner: owner,
                    start: new Date().toISOString(),
                    end: addDays(new Date(), 7).toISOString(),
                    assignee: assignee,
                },
                appointment: appointment,
            },
            transaction,
        );
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const beginEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const appointmentType = data.type || {};
    const Appointment = data.appointment;
    const appointmentTitle = Appointment.description || Appointment.bnAppointmentType?.display || 'Session';
    // this flow can be applied to a single patient or all patients
    const patients = data.patient ? [data.patient] : await getPatients(Appointment);
    // if no patients in appointment we can exit
    if (!patients.length) return [];

    // GROUP ENCOUNTER
    const isGroupEncounter = appointmentType.group;
    const createGroupEncounter = isGroupEncounter && !data.patient;
    const groupEncounterTaskId = isGroupEncounter ? uuidv4() : undefined;

    // set provider and assignees
    const provider = getOwner(Appointment);
    const assignee = Appointment.participant.filter((item) => item.actor?.resourceType === 'Practitioner')?.map((item) => item.actor) || [];

    const encounterLocation = [{ location: getLocation(Appointment) || { display: 'Unknown' } }];
    const encounterPeriod = { start: Appointment.start, end: Appointment.end };
    const encounterProvider = [
        {
            type: [{ coding: [{ code: 'PPRF' }] }],
            individual: {
                id: provider.id,
                resourceType: provider.resourceType,
                display: provider.display,
            },
            period: encounterPeriod,
        },
    ];
    const supervisorParticipant = getDefaultSupervisorParticipant();
    if (supervisorParticipant) encounterProvider.push(supervisorParticipant);

    // get current diagnosis for patients
    const patientIds = patients.map((p) => p.actor.id).join(',');
    let diagnosis = (await $httpFhirApi.get('/Condition?subject=' + patientIds + '&clinical-status=active&category=encounter-diagnosis')).data;

    const formIdMap = [...new Array(Appointment.form.length)].map(() => uuidv4());

    // process flow for each target resource
    let results = [];
    let patientIndex = 0;
    transaction = await updateEvent(
        {
            appointment: Appointment,
        },
        transaction,
    );

    // Fetch jobTitle/credentials for Practitioner participants (once, shared across patients)
    const allOtherParticipants = getOtherAppointmentParticipants(Appointment, null);
    const practitionerIds = allOtherParticipants.filter((p) => p.actor?.resourceType === 'Practitioner').map((p) => p.actor.id);
    const practitionerDetails = {};
    if (practitionerIds.length) {
        const practResult = (await $httpFhirApi.get('/Practitioner?_id=' + practitionerIds.join(',') + '&_elements=jobTitle,credentials')).data;
        practResult?.forEach((pract) => {
            practitionerDetails[pract.id] = { jobTitle: pract.jobTitle, credentials: pract.credentials };
        });
    }

    // Fetch relationships/roles for additional participants (BN_Relationship + CareTeam per subject patient)
    const participantRelationships = {};
    await Promise.all(patients.map((patient) => fetchRelationshipsForSubject(patient.actor.id, participantRelationships)));

    for (const patient of patients) {
        // set up the encounter resource for each patient
        let patientDiagnosis = diagnosis.filter((cond) => cond.subject.id === patient.actor.id).map((cond) => new EncounterDiagnosis({ condition: cond }));

        // Convert other appointment participants into FHIR Encounter participant entries with their relationships and credentials
        const otherParticipants = getOtherAppointmentParticipants(Appointment, patient.actor.id);
        const partParticipants = otherParticipants.map((p) => buildEncounterParticipant(p, encounterPeriod, practitionerDetails, participantRelationships));
        const encounterParticipant = [...encounterProvider, ...partParticipants];

        const encounterResource = new Encounter({
            id: uuidv4(),
            appointment: [{ id: Appointment.id, display: appointmentTitle, resourceType: 'Appointment' }],
            status: 'in-progress',
            class: { code: 'AMB', display: 'ambulatory' },
            type: [
                {
                    coding: [{ system: 'http://fhir.bestnotes.com/encounterTypeToken', code: appointmentType.token, display: appointmentTitle }],
                },
                {
                    coding: [{ system: 'http://fhir.bestnotes.com/encounterTypeUrl', code: appointmentType.url }],
                },
            ],
            subject: patient.actor,
            diagnosis: patientDiagnosis,
            participant: encounterParticipant,
            period: {
                start: Appointment.start,
                end: Appointment.end,
            },
            location: encounterLocation,
            episodeOfCare: [patient.episodeOfCare],
            partOf: patient.encounter,
            selfPay: patient.selfPay,
        });

        const start = new Date().toISOString();
        const end = addDays(new Date(), 7).toISOString();

        const flowAssignee = isGroupEncounter ? null : assignee;

        // add encounter
        transaction.push(crud.post(encounterResource));

        let formIndex = 0;
        for (const form of Appointment.form) {
            // only need to create the parent group task once per form, not per patient
            if (createGroupEncounter && patientIndex === 0) {
                transaction = await addFormTask(
                    {
                        task: {
                            id: formIdMap[formIndex],
                            owner: provider,
                            start: start,
                            end: end,
                            assignee: assignee,
                            groupTask: groupEncounterTaskId,
                            parentGroupTask: groupEncounterTaskId,
                        },
                        form: form,
                        appointment: Appointment,
                    },
                    transaction,
                );
            }

            transaction = await addFormTask(
                {
                    task: {
                        owner: provider,
                        for: patient.actor,
                        start: start,
                        end: end,
                        assignee: flowAssignee,
                        groupTask: groupEncounterTaskId,
                        groupParticipantTask: formIdMap[formIndex],
                    },
                    form: form,
                    appointment: Appointment,
                    encounter: encounterResource,
                },
                transaction,
            );

            formIndex++;
        }

        // if applied to a single patient then return the transaction
        if (data.patient && existingTransaction !== false) {
            results = await postTransaction(transaction, true);
        } else {
            const localResults = await postTransaction(transaction);
            transaction = [];
            results[patientIndex.toString()] = { patient: patient.actor, encounter: encounterResource, groupTaskId: groupEncounterTaskId, results: localResults };
            patientIndex++;
        }
    }

    return results;
};

const addFormTask = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const task = data.task;
    task.appointment = data.appointment;

    const forms = data.form ? [data.form] : data.appointment.form;

    if (data.encounter?.id) {
        task.encounter = { resourceType: 'Encounter', id: data.encounter.id };
    }

    // regular task - add all appointment forms
    if (data.task.groupParticipantTask || forms.length) {
        for (const form of forms) {
            task.form = form;
            const { addTask } = useTaskFlow();
            transaction = await addTask(task, transaction);
        }
    } else if (data.encounter?.id) {
        // this is an encounter with no forms, so we create a placeholder task so that the encounter
        // renders on the Kanban board
        task.status = 'in-progress';
        // remove extra assignees
        task.assignee = [task.owner];
        // process subflow - return transaction
        const { addTask } = useTaskFlow();
        transaction = await addTask(task, transaction);
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const removeTaskAppt = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (data.appointment) {
        let tasks = await $httpFhirApi.get('Task?based-on=' + data.appointment.id);
        // Loop through and create transaction to delete primary task and all assignments/documents if any
        tasks.data.forEach((task) => {
            /**
             * Delete 'focus' items associated with the task depending on their type. Initially we are only working with
             * QuestionnaireResponse as the focus so they will be deleted. As other 'focus' resources are defined this code will
             * need to be updated to determine if those resources should be deleted with the task.
             */
            const resourcesToDelete = ['QuestionnaireResponse'];
            if (task.focus?.id && resourcesToDelete.includes(task.focus?.resourceType)) {
                transaction.push(crud.delete(task.focus));
            }
            // delete task
            transaction.push(crud.delete(task));
        });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const resetEvent = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const appointment = data.appointment;

    transaction = await updateEvent({ appointment }, transaction);

    transaction = await removeTaskAppt({ appointment }, transaction);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const appointmentStatus = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // confirm all
    if (data.status === 'confirm-all') {
        data.appointment.status = 'booked';
        setAllParticipantStatus(data.appointment, 'booked');
    }
    // unconfirm all
    else if (data.status === 'unconfirm-all') {
        data.appointment.status = 'pending';
        setAllParticipantStatus(data.appointment, 'pending');
    }
    // check-in all
    if (data.status === 'checkin-all') {
        data.appointment.status = 'checked-in';
        setAllParticipantStatus(data.appointment, 'checked-in');
    }
    // check-in all
    else if (data.status === 'uncheckin-all') {
        data.appointment.status = 'pending';
        setAllParticipantStatus(data.appointment, 'pending');
    }
    // cancel
    else if (data.status === 'cancelled') {
        data.appointment.status = 'cancelled';
    }
    // reset to pending
    else if (data.status === 'pending') {
        data.appointment.status = 'pending';
    } else if (['payment-processing', 'payment-paid'].includes(data.status)) {
        data.appointment.paymentStatus = data.appointment.paymentStatus === data.status ? undefined : data.status;
    }
    // set reason
    if (data.reason?.code) {
        data.appointment.cancelationReason = {
            text: data.reason.text,
            coding: [{ system: 'http://fhir.bestnotes.com/apptStatusReason', code: data.reason.code, display: data.reason.code === 'other' ? 'Other' : data.reason.text }],
        };
    } else {
        data.appointment.cancelationReason = undefined;
    }

    // create transaction entry
    transaction.push(crud.patch(data.appointment));

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const participantStatus = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // set the specified participant status (this updates the data.appointment object to be patched)
    setParticipantStatus(data.appointment, data.actor.id, data.status, data.reason);
    transaction.push(crud.patch(data.appointment));

    // if the appointment/event is in-progress then we may need to update associated resources
    if (data.appointment.status === 'fulfilled') {
        const participant = data.appointment.participant.find((item) => item.actor.id === data.actor.id);
        if (isPatient(data.actor) && participant.encounter?.id) {
            // clean up existing tasks/encounters if status changes to declined
            if (['cancelled', 'noshow'].includes(data.status)) {
                // remove the encounter resources if they exist
                const encounter = await $httpFhirApi.get('Encounter?subject=' + data.actor.id + '&appointment=' + data.appointment.id + '&_revinclude=Procedure:encounter');
                encounter.data.forEach((resource) => {
                    transaction.push(crud.delete(resource));
                });
                // remove tasks and related data
                const task = await $httpFhirApi.get('Task?subject=' + data.actor.id + '&based-on=' + data.appointment.id + '&_revinclude=Task:part-of&_include=Task:focus:QuestionnaireResponse');
                task.data.forEach((resource) => {
                    // remove related resource
                    transaction.push(crud.delete(resource));
                });
            }
            // begin encounter if the patient shows up (change from declined to accepted/tentative...AKA: checked-in)
            else if (data.status === 'checked-in') {
                const encounterData = {
                    patient: participant,
                    event: data.appointment,
                };
                transaction = await beginEncounter(encounterData, transaction);
            }
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const addRecurringEvents = async (appointment, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    appointment.seriesId = uuidv4();
    // create all instances
    let start, end;
    let duration = differenceInMinutes(new Date(appointment.end), new Date(appointment.start));
    let rule = RRule.fromString(appointment.seriesDescription);
    rule.all().forEach((instance) => {
        /**
         * Be very careful here - there is a lot of date juggling to deal with limitations
         * in the RRule library in regard to timezones and date offsets
         */
        start = format(new Date(instance.toUTCString().replace('GMT', '')), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
        end = format(addMinutes(new Date(start), duration), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
        appointment.start = timezoneApi.setGlobalTimeToDST(start);
        appointment.end = timezoneApi.setGlobalTimeToDST(end);
        transaction.push(crud.post(appointment));
    });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const convertEventToRecurring = async (appointment, existingTransaction = false) => {
    let transaction = existingTransaction || [];
    // series description (recurring definition) is required
    if (appointment.seriesDescription) {
        // delete original event
        transaction.push(crud.delete(appointment));
        // create new event and replicate it using the seriesDescription
        const props = JSON.parse(JSON.stringify(appointment));
        delete props.id;
        const newAppointment = new Appointment(props);

        transaction = await addRecurringEvents(newAppointment, transaction);
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const updateRecurringEvent = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];
    // if series has been disabled then remove all pending instances and only leave the original
    if (data.appointment.seriesDescription === undefined) {
        // delete all pending events in the series
        const pendingEvents = await getPendingEvents(data.appointment);
        pendingEvents.forEach((event) => {
            if (event.id !== data.appointment.id) {
                transaction.push(crud.delete(event));
            }
        });
        // create single event out of original
        data.appointment.seriesId = undefined;
        transaction.push(crud.patch(data.appointment));
    }
    // replace a current and future events with a new series
    else if (data.appointment.temp.recurrenceHasChanged && data.appointment.seriesDescription) {
        transaction = replaceRecurringEvents(data.appointment, transaction);
    }
    // apply changes to specific instances (data.applyTo)
    else if (data.applyTo) {
        // make standalone - detach instance from series
        if (data.applyTo === 'detach') {
            data.appointment.seriesId = undefined;
            data.appointment.seriesDescription = undefined;
            transaction.push(crud.patch(data.appointment));
        }
        // apply to pending instances
        else if (data.applyTo === 'pending') {
            // load pending events before manipulating source event
            const pendingEvents = await getPendingEvents(data.appointment);
            // create a new seriesId since we are detaching this update from the original series
            data.appointment.seriesId = uuidv4();
            // calculate time changes
            let startTime = timezoneApi.getLocalDateTime(new Date(data.appointment.start), 'HH:mm:ss', localTimeZone);
            let eventDuration = differenceInMinutes(new Date(data.appointment.end), new Date(data.appointment.start));
            // reset all participant statuses to 'pending'
            data.appointment.participant?.forEach((participant) => {
                participant.appointmentParticipantStatus = 'pending';
                participant.statusReason = undefined;
            });

            // patch the existing event
            transaction.push(crud.patch(data.appointment));

            // extract event details from source appointment to be used to create replacement instances
            let eventProps = data.appointment.toJSON();
            // remove id
            eventProps.id = undefined;
            // replace pending instances
            pendingEvents.forEach((instance) => {
                // skip the primary event if included since it will be updated by itself
                if (instance.id === data.appointment.id) return;
                // delete old instance
                transaction.push(crud.delete(instance));
                // create a new instance using source event
                const newEvent = new Appointment(eventProps);
                // retain status of existing instance
                newEvent.status = instance.status;
                // set start/end time changes
                let startDate, start, end;
                startDate = timezoneApi.getLocalDateTime(new Date(instance.start), 'yyyy-MM-dd', localTimeZone);
                start = timezoneApi.format(new Date(startDate + 'T' + startTime), "yyyy-MM-dd'T'HH:mm:ss", localTimeZone);
                end = addMinutes(new Date(start), eventDuration);
                newEvent.start = timezoneApi.setGlobalTimeToDST(start);
                newEvent.end = timezoneApi.setGlobalTimeToDST(end);
                // save new instance
                transaction.push(crud.post(newEvent));
            });
        }
    }
    // if nothing else matches then ???
    else {
        /**
         * Options include:
         *      - update instance
         *      - detach instance
         *      - other?
         */
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const replaceRecurringEvents = async function (appointment, existingTransaction = false) {
    let transaction = existingTransaction || [];
    // delete all pending events from old recurrence
    const pendingEvents = await getPendingEvents(appointment);
    pendingEvents.forEach((instance) => {
        transaction.push(crud.delete(instance));
    });
    const eventProps = appointment.toJSON();
    delete eventProps.id;
    delete eventProps.recurrenceHasChanged;
    // reset all participant statuses to 'pending'
    let participants = [];
    eventProps.participant?.forEach((participant) => {
        participant.appointmentParticipantStatus = 'pending';
        participants.push(participant);
    });
    eventProps.participant = participants;
    const newEvent = new Appointment(eventProps);

    transaction = await addRecurringEvents(newEvent, transaction);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const addNote = (data) => {
    const [event, comment, save] = data;
    const authStore = useAuthStore();
    // author details
    let author = authStore.practitioner;
    let authorDetail = {
        Reference: {
            resourceType: author.resourceType,
            id: author.id,
            display: author.fullName,
        },
    };
    // add comment to existing comments and return for saving
    let notes = event.note;
    notes.push({ author: authorDetail, text: comment, time: new Date().toISOString() });
    if (save) {
        let transaction = { type: 'transaction', entry: [crud.patch(event)] };
        postTransaction(transaction);
    }
    return notes;
};

const getPatients = async (appointment) => {
    return appointment.participant?.filter((p) => {
        return p.actor?.resourceType === 'Patient' && p?.encounter && !['cancelled', 'noshow'].includes(p.appointmentParticipantStatus);
    });
};
const getLocation = function (appointment) {
    const item = appointment.participant?.find((p) => p.actor?.resourceType === 'Location');
    return item?.actor;
};

const getPendingEvents = async function (appointment) {
    let futureInstances = timezoneApi.getGlobalDateTime(new Date(appointment.__originalObjJson.start), "yyyy-MM-dd'T'HH:mm:ss", localTimeZone);
    const results = await $httpFhirApi.get('Appointment?' + '_sort=date&seriesId=' + appointment.seriesId + '&status=pending&date=ge' + futureInstances);
    return results.data;
};
const getOwner = function (appointment) {
    const item = appointment.participant?.find((p) => p.typeZero?.coding[0]?.code === 'PPRF');
    return item?.actor;
};

/** Other participants: not PPRF, not Location, not the given subject patient (for this encounter). Used for PART participants on Encounter. */
const getOtherAppointmentParticipants = function (appointment, subjectPatientId) {
    if (!appointment?.participant?.length) return [];
    const provider = getOwner(appointment);
    const providerId = provider?.id;
    return appointment.participant.filter((p) => {
        const typeCode = p.type?.[0]?.coding?.[0]?.code ?? p.typeZero?.coding?.[0]?.code;
        if (typeCode === 'PPRF' || (providerId && p.actor?.id === providerId)) return false;
        if (p.actor?.resourceType === 'Location' || typeCode === 'ro') return false;
        if (p.actor?.resourceType === 'Patient' && p.actor?.id === subjectPatientId) return false;
        return true;
    });
};

const PART_PARTICIPATION_TYPE = [
    {
        coding: [
            {
                code: 'PART',
                display: 'Participation',
                system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
            },
        ],
    },
];

// Build a FHIR Encounter participant entry from an appointment participant
function buildEncounterParticipant(p, period, practitionerDetails, relationshipMap) {
    const actorRef = { id: p.actor.id, resourceType: p.actor.resourceType, display: p.actor.display };
    const relationship = relationshipMap[p.actor.id] || undefined;
    const actorKey = p.actor?.resourceType === 'Patient' ? 'individualPatient' : 'individual';

    let partType = PART_PARTICIPATION_TYPE;
    if (p.actor?.resourceType === 'Practitioner') {
        const titleCredentials = buildJobTitleCredentials(practitionerDetails[p.actor.id] || {});
        if (titleCredentials) {
            partType = [{ ...PART_PARTICIPATION_TYPE[0], text: titleCredentials }];
        }
    }

    return { type: partType, period, [actorKey]: actorRef, relationshipToPatient: relationship };
}
const setAllParticipantStatus = function (appointment, status) {
    appointment.participant.forEach((e) => {
        // don't update cancelled/noshow participants
        if (['cancelled', 'noshow'].includes(e.appointmentParticipantStatus)) {
            return;
        }
        setParticipantStatus(appointment, e.actor.id, status);
    });
};

const setParticipantStatus = function (appointment, participantId, status, reason) {
    let participant = appointment.participant.find((item) => item.actor.id === participantId);

    // set status to local object
    participant.appointmentParticipantStatus = status;
    participant.statusReason = undefined;

    // process noshow/cancellation
    if (['cancelled', 'noshow'].includes(status)) {
        if (reason?.code) {
            participant.statusReason = {
                text: reason.text,
                coding: [{ system: 'http://fhir.bestnotes.com/apptStatusReason', code: reason.code, display: reason.code === 'other' ? 'Other' : reason.text }],
            };
        }
    }
};

export function useAppointmentFlow() {
    return { createEvent, updateEvent, deleteEvent, resetEvent, beginEncounter, beginEvent, participantStatus, appointmentStatus, addNote, addFormTask };
}
